import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import TopBar from "./TopBar";
import { trackEvent } from "../analytics";
import Footer from "./Footer";

const PricingPage = ({ standalone = false }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userPlan, setUserPlan] = useState(null); // Track user's current plan
  const [statusLoading, setStatusLoading] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    trackEvent("Viewed Pricing");
    const fetchPlans = async () => {
      setLoading(true);
      try {
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/subscription/plans`,
          {
            withCredentials: true,
            headers,
          }
        );
        setPlans(res.data.plans || []);
      } catch (err) {
        setError("Failed to load plans");
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, [token]);

  useEffect(() => {
    // Fetch user's current subscription status if logged in
    const fetchStatus = async () => {
      if (!token) return;
      setStatusLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/subscription/status`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setUserPlan(res.data.subscription?.planId || null);
      } catch (err) {
        setUserPlan(null);
      } finally {
        setStatusLoading(false);
      }
    };
    fetchStatus();
  }, [token]);

  const handleSubscribe = async (plan) => {
    if (!token) {
      toast.info("Please log in or sign up to subscribe to a paid plan.");
      navigate("/signup");
      return;
    }
    try {
      // Only call /subscription/subscribe now
      const subscribeRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/subscription/subscribe`,
        { planId: plan._id },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      trackEvent("Upgrade Plan", { plan: plan.name, price: plan.price });
      const subscription = subscribeRes.data.subscription;

      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        throw new Error("Razorpay Checkout.js not loaded");
      }

      // Check if Razorpay key is available
      if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
        throw new Error("Razorpay key not configured");
      }

      // 3. Open Razorpay Checkout
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Razorpay key
        subscription_id: subscription.id,
        name: "InstaShop",
        description: `Subscribe to ${plan.name}`,
        handler: function (response) {
          toast.success("Payment successful! Subscription activated.");
          window.location.reload();
        },
        theme: { color: "#007bff" },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Failed to start subscription/payment. Please try again."
      );
    }
  };

  return (
    <>
      {standalone ? (
        <Footer
          supportEmail={
            process.env.REACT_APP_SUPPORT_EMAIL || "noreply.reeltale@gmail.com"
          }
          setFeedbackOpen={() => {}} // No feedback modal in PricingPage
        >
          <TopBar />
          {token && (
            <div className="px-5 md:px-10 pt-4">
              <Tooltip title="Back to Dashboard">
                <IconButton
                  onClick={() => navigate("/dashboard")}
                  className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            </div>
          )}
          <div id="pricing">
            <Box className={`pt-6 sm:pt-8 pb-12 sm:pb-16 md:pb-20 lg:pb-24 px-5 md:px-10 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 ${token ? '' : 'pt-12 sm:pt-16 md:pt-20 lg:pt-24'}`}>
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12 sm:mb-16">
                  <div className="inline-block mb-4 px-4 py-2 bg-primary-100 dark:bg-primary-900/30 rounded-full">
                    <span className="text-primary-700 dark:text-primary-300 text-sm font-semibold">
                      💰 Choose Your Plan
                    </span>
                  </div>
                  <Typography
                    variant="h3"
                    component="h2"
                    className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 text-gray-900 dark:text-white"
                  >
                    Simple Pricing, Powerful Features
                  </Typography>
                  <div className="max-w-2xl mx-auto">
                    <Typography
                      variant="body1"
                      className="text-base sm:text-lg text-gray-600 dark:text-gray-400 text-center"
                    >
                      Choose the perfect plan for your business needs. All plans include our core features.
                    </Typography>
                  </div>
                </div>

                <div className="flex justify-center items-center gap-6 flex-wrap">
                  {loading || statusLoading ? (
                    <CircularProgress />
                  ) : error ? (
                    <Typography className="text-red-600 dark:text-red-400">{error}</Typography>
                  ) : (
                    plans.map((plan) => (
                      <PricingCard
                        key={plan._id}
                        title={plan.name}
                        price={
                          plan.name === "Free"
                            ? "₹0"
                            : `${plan.currency === "INR" ? "₹" : "$"}${plan.price}`
                        }
                        features={plan.features}
                        buttonText={
                          plan.name === "Free"
                            ? userPlan === plan._id
                              ? "You are on the Free plan"
                              : "Get Started for Free"
                            : `Subscribe to ${plan.name}`
                        }
                        buttonStyle={{
                          backgroundColor:
                            plan.name === "Pro"
                              ? "#0ea5e9"
                              : plan.name === "Free"
                              ? "#007bff"
                              : "#ffc107",
                          color:
                            plan.name === "Pro"
                              ? "#fff"
                              : plan.name === "Free"
                              ? "#fff"
                              : "#333",
                        }}
                        onSubscribe={() => handleSubscribe(plan)}
                        isFree={plan.name === "Free"}
                        isCurrentFree={plan.name === "Free" && userPlan === plan._id}
                        isPro={plan.name === "Pro"}
                      />
                    ))
                  )}
                </div>
              </div>
            </Box>
          </div>
        </Footer>
      ) : (
        <>
          <div id="pricing">
            <Box className="py-12 sm:py-16 md:py-20 lg:py-24 px-5 md:px-10 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12 sm:mb-16">
                  <div className="inline-block mb-4 px-4 py-2 bg-primary-100 dark:bg-primary-900/30 rounded-full">
                    <span className="text-primary-700 dark:text-primary-300 text-sm font-semibold">
                      💰 Choose Your Plan
                    </span>
                  </div>
                  <Typography
                    variant="h3"
                    component="h2"
                    className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 text-gray-900 dark:text-white"
                  >
                    Simple Pricing, Powerful Features
                  </Typography>
                  <div className="max-w-2xl mx-auto">
                    <Typography
                      variant="body1"
                      className="text-base sm:text-lg text-gray-600 dark:text-gray-400 text-center"
                    >
                      Choose the perfect plan for your business needs. All plans include our core features.
                    </Typography>
                  </div>
                </div>

                <div className="flex justify-center items-center gap-6 flex-wrap">
                  {loading || statusLoading ? (
                    <CircularProgress />
                  ) : error ? (
                    <Typography className="text-red-600 dark:text-red-400">{error}</Typography>
                  ) : (
                    plans.map((plan) => (
                      <PricingCard
                        key={plan._id}
                        title={plan.name}
                        price={
                          plan.name === "Free"
                            ? "₹0"
                            : `${plan.currency === "INR" ? "₹" : "$"}${plan.price}`
                        }
                        features={plan.features}
                        buttonText={
                          plan.name === "Free"
                            ? userPlan === plan._id
                              ? "You are on the Free plan"
                              : "Get Started for Free"
                            : `Subscribe to ${plan.name}`
                        }
                        buttonStyle={{
                          backgroundColor:
                            plan.name === "Pro"
                              ? "#0ea5e9"
                              : plan.name === "Free"
                              ? "#007bff"
                              : "#ffc107",
                          color:
                            plan.name === "Pro"
                              ? "#fff"
                              : plan.name === "Free"
                              ? "#fff"
                              : "#333",
                        }}
                        onSubscribe={() => handleSubscribe(plan)}
                        isFree={plan.name === "Free"}
                        isCurrentFree={plan.name === "Free" && userPlan === plan._id}
                        isPro={plan.name === "Pro"}
                      />
                    ))
                  )}
                </div>
              </div>
            </Box>
          </div>
        </>
      )}
    </>
  );
};

PricingPage.propTypes = {
  standalone: PropTypes.bool,
};

const PricingCard = ({
  title,
  price,
  features,
  buttonText,
  buttonStyle,
  onSubscribe,
  isFree,
  isCurrentFree,
  isPro,
}) => (
  <Card
    className={`card-modern h-full p-8 flex flex-col transition-all duration-300 hover:scale-105 hover:shadow-2xl relative ${
      isPro ? "border-2 border-primary-500 dark:border-primary-400 ring-4 ring-primary-200 dark:ring-primary-900/50" : "border border-gray-200 dark:border-gray-700"
    }`}
    style={{ maxWidth: 380, minHeight: 550 }}
  >
    {isPro && (
      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-primary-600 dark:bg-primary-500 text-white text-xs font-bold rounded-full shadow-lg z-10">
        POPULAR
      </div>
    )}
    <CardContent className="flex-grow flex flex-col p-0">
      <Typography variant="h5" className="font-bold text-center mb-4 text-gray-900 dark:text-white text-2xl">
        {title}
      </Typography>
      <Divider className="my-4" />
      <Typography
        variant="h4"
        className="font-extrabold text-center mb-2 text-primary-600 dark:text-primary-400 text-4xl"
      >
        {price}
        {!isFree && <span className="text-lg text-gray-600 dark:text-gray-400 font-normal">/month</span>}
      </Typography>
      {title === "Pro" && (
        <Typography variant="body2" className="text-center text-gray-500 dark:text-gray-400 font-semibold mb-2">
          + 5% sales commission
        </Typography>
      )}
      {title === "All-In" && (
        <Typography variant="body2" className="text-center text-gray-500 dark:text-gray-400 font-semibold mb-2">
          + 5% sales commission
        </Typography>
      )}
      <List className="mt-4 mb-6 flex-grow space-y-3">
        {features.map((feature, index) => (
          <ListItem key={index} className="px-0 py-1">
            <span className="text-green-500 mr-2">✓</span>
            <span className="text-gray-700 dark:text-gray-300">{feature}</span>
          </ListItem>
        ))}
      </List>
      <Button
        variant="contained"
        className="w-full py-3 rounded-xl font-bold text-base normal-case transition-all duration-200 hover:shadow-lg transform hover:scale-105"
        style={buttonStyle}
        onClick={isCurrentFree ? undefined : onSubscribe}
        disabled={isCurrentFree}
      >
        {buttonText}
      </Button>
    </CardContent>
  </Card>
);

PricingCard.propTypes = {
  title: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  features: PropTypes.array.isRequired,
  buttonText: PropTypes.string.isRequired,
  buttonStyle: PropTypes.object.isRequired,
  onSubscribe: PropTypes.func.isRequired,
  isFree: PropTypes.bool,
  isCurrentFree: PropTypes.bool,
  isPro: PropTypes.bool,
};

export default PricingPage;
